//npm run deploy -- --m "v# --commit message"
import './App.css';
import { createBrowserRouter, RouterProvider, useNavigate } from 'react-router-dom';
import { getDocs, collection, getDoc, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { FirebaseProvider, useFirebase } from './contexts/FirebaseContext';

// MK8DX Components
import Mk8dxApp from './mk8dx/Mk8dxApp';
import CupBuilder from './mk8dx/pages/CupBuilder/CupBuilder';
import Browse from './mk8dx/pages/Browse/Browse';
import BrowseCupId from './mk8dx/pages/Browse/BrowseCupId';
import getTracks from './mk8dx/functions/getTrackData';

// World Components
import WorldApp from './world/WorldApp';
import WorldCupBuilder from './world/pages/WorldCupBuilder/WorldCupBuilder';
import WorldBrowse from './world/pages/WorldBrowse/WorldBrowse';
import WorldBrowseCupId from './world/pages/WorldBrowse/WorldBrowseCupId';
import getWorldTracks from './world/functions/getWorldTracks';

// Shared Auth Components
import SignUp from './shared/pages/auth/SignUp';
import SignIn from './shared/pages/auth/SignIn';
import ForgotPassword from './shared/pages/auth/ForgotPassword';

// Shared Profile Components
import Profile from './shared/pages/Profile/Profile';
import ProfileEdit from './shared/pages/Profile/ProfileEdit';
import ProfileLayout from './shared/components/ProfileLayout/ProfileLayout';

const NotFound = () => {
  const navigate = useNavigate();
  setTimeout(() => {
    navigate("/");
  }, 5000);
  return <h1>Page not found. Redirecting to homepage</h1>
}

function AppRouter() {
  const { auth, fbApp, firestore } = useFirebase();

  const router = createBrowserRouter([
    {
      path: "*",
      element: <NotFound />
    },
    {
      path: "/signin",
      element: <SignIn />,
    },
    {
      path: "/signup",
      element: <SignUp />
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />
    },
    // Shared Profile Routes
    {
      path: "profile",
      element: <ProfileLayout auth={auth} fbApp={fbApp} />,
      children: [
        {
          path: ":username",
          element: <Profile />,
          errorElement: <div id="main-container"><p>Profile not found at this URL.</p></div>,
          loader: async ({ params }) => {
            // Query by username instead of UID
            const usersQuery = query(
              collection(firestore, "users"),
              where("displayNameLower", "==", params.username.toLowerCase()),
              limit(1)
            );
            const userSnapshot = await getDocs(usersQuery);

            if (userSnapshot.empty) {
              throw new Error("no profile found");
            }

            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();

            // Fetch all cups in parallel instead of sequentially
            const cupPromises = userData.cups?.map(cupRef => getDoc(cupRef)) || [];
            const worldCupPromises = userData.worldCups?.map(cupRef => getDoc(cupRef)) || [];

            const [cupDocs, worldCupDocs] = await Promise.all([
              Promise.all(cupPromises),
              Promise.all(worldCupPromises)
            ]);

            const cups = cupDocs.map(cupDoc => ({
              id: cupDoc.id,
              tracks: getTracks(cupDoc.data().tracks),
              cupName: cupDoc.data().cupName
            }));

            const worldCups = worldCupDocs.map(cupDoc => ({
              id: cupDoc.id,
              tracks: getWorldTracks(cupDoc.data().tracks),
              cupName: cupDoc.data().cupName
            }));

            return { ...userData, cups, worldCups, id: userDoc.id };
          }
        },
        {
          path: ":username/edit",
          element: <ProfileEdit />,
          loader: async ({ params }) => {
            // Query by username instead of UID
            const usersQuery = query(
              collection(firestore, "users"),
              where("displayNameLower", "==", params.username.toLowerCase()),
              limit(1)
            );
            const userSnapshot = await getDocs(usersQuery);

            if (userSnapshot.empty) {
              return { cups: [], worldCups: [], id: null, displayName: null };
            }

            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();

            // Fetch all cups in parallel instead of sequentially
            const cupPromises = userData.cups?.length > 0
              ? userData.cups.map(cupRef => getDoc(cupRef))
              : [];
            const worldCupPromises = userData.worldCups?.length > 0
              ? userData.worldCups.map(cupRef => getDoc(cupRef))
              : [];

            const [cupDocs, worldCupDocs] = await Promise.all([
              Promise.all(cupPromises),
              Promise.all(worldCupPromises)
            ]);

            const cups = cupDocs.map(cupDoc => ({
              id: cupDoc.id,
              tracks: getTracks(cupDoc.data().tracks),
              cupName: cupDoc.data().cupName
            }));

            const worldCups = worldCupDocs.map(cupDoc => ({
              id: cupDoc.id,
              tracks: getWorldTracks(cupDoc.data().tracks),
              cupName: cupDoc.data().cupName
            }));

            return { ...userData, cups, worldCups, id: userDoc.id };
          }
        }
      ]
    },
    // Mario Kart World Routes (root path)
    {
      element: <WorldApp auth={auth} fbApp={fbApp} />,
      children: [
        {
          path: "/",
          element: <WorldCupBuilder />
        },
        {
          path: "/browse",
          element: <WorldBrowse />,
          loader: async () => {
            // Only load first 24 cups initially
            const q = query(
              collection(firestore, "worldCups"),
              orderBy("timestamp", "desc"),
              limit(24)
            );
            const response = await getDocs(q);
            const cupData = [];
            response.forEach((doc) => {
              const docData = doc.data();
              cupData.push({
                id: doc.id,
                ...docData,
                tracks: docData.tracks // Keep raw format for now, will convert in component
              });
            });
            return cupData;
          }
        },
        {
          path: "/browse/cup/:cupid",
          element: <WorldBrowseCupId />,
          errorElement: <div id="main-container"><p>Cup at this URL not found.</p></div>,
          loader: async ({ params }) => {
            const cup = await getDoc(doc(firestore, "worldCups", params.cupid));
            if (cup.exists()) {
              const cupData = cup.data();
              const user = await getDoc(doc(firestore, "users", cupData.creator));
              const userData = user.data();

              return {
                id: cup?.id,
                tracks: cupData.tracks, // Raw format {start, end}
                trackList: cupData.trackList,
                creator: userData.displayName,
                creatorId: cupData.creator,
                cupName: cupData.cupName
              };
            } else {
              throw new Error("no cup found");
            }
          }
        }
      ]
    },
    // Mario Kart 8 Deluxe Routes
    {
      path: "/mk8dx",
      element: <Mk8dxApp auth={auth} fbApp={fbApp} />,
      children: [
        {
          index: true,
          element: <CupBuilder />
        },
        {
          path: "browse",
          element: <Browse />,
          loader: async () => {
            // Only load first 24 cups initially
            // More will be loaded on-demand as user browses
            const q = query(
              collection(firestore, "cups"),
              orderBy("timestamp", "desc"),
              limit(24)
            );
            const response = await getDocs(q);
            const cupData = [];
            response.forEach((doc) => {
              const docData = doc.data();
              cupData.push({ id: doc.id, ...doc.data(), tracks: getTracks(docData.tracks) })
            });
            return cupData;
          }
        },
        {
          path: "browse/cup/:cupid",
          element: <BrowseCupId />,
          errorElement: <div id="main-container"><p>Cup at this URL not found.</p></div>,
          loader: async ({ params }) => {
            const cup = await getDoc(doc(firestore, "cups", params.cupid));
            if (cup.exists()) {
              const cupData = cup.data();
              const user = await getDoc(doc(firestore, "users", cupData.creator));
              const userData = user.data();

              return {
                id: cup?.id,
                tracks: getTracks(cupData.tracks),
                creator: userData.displayName,
                creatorId: cupData.creator,
                cupName: cupData.cupName
              };

            } else {
              throw new Error("no cup found");
            }
          }
        },
      ]
    }
  ]);

  return <RouterProvider router={router} />;
}

function App() {
  return <FirebaseProvider>
    <AppRouter />
  </FirebaseProvider>
}

export default App;
